import { AddIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { type LibraryElementTypeDto, useListLibraryElementsQuery } from "../../autogen/bff-api";

// t('Pick from the library, or write a new requirement — it is saved to the library for later use.')
// t('The library has nothing of this kind yet — write the first one.')
// t('From the library ({{count}})') t('New requirement')

interface Props {
  /** "Ingen minstekrav i denne anskaffelsen" — only the section knows what to call its own contents. */
  title: string;
  type: LibraryElementTypeDto;
  onAddFromLibrary: () => void;
  onWriteNew: () => void;
}

/**
 * An empty section is the one place where "add" deserves the whole surface rather than a button in the
 * heading. It also says what is waiting in the library, so the choice is informed before the panel opens.
 */
export const SectionEmptyState = ({ title, type, onAddFromLibrary, onWriteNew }: Props) => {
  const { t } = useTranslation();
  const { data } = useListLibraryElementsQuery({});
  const ofType = (data?.elements ?? []).filter((e) => e.content.type === type);

  return (
    <Box
      borderWidth="1px"
      borderStyle="dashed"
      borderColor="smBorder"
      borderRadius="lg"
      bg="smBackground"
      py="10"
      px="6"
      textAlign="center"
    >
      <Text fontWeight="bold" mb="1">
        {title}
      </Text>
      <Text fontSize="sm" color="smMuted" maxW="md" mx="auto" mb="5">
        {ofType.length === 0
          ? t("The library has nothing of this kind yet — write the first one.")
          : t("Pick from the library, or write a new requirement — it is saved to the library for later use.")}
      </Text>
      {/* Both ways in, side by side: neither is reached through the other. */}
      <Flex gap="3" justify="center" wrap="wrap">
        {/* Same size as the section heading's actions: an empty section is not a bigger decision. */}
        {ofType.length > 0 && (
          <Button size="sm" colorScheme="teal" onClick={onAddFromLibrary}>
            {t("From the library ({{count}})", { count: ofType.length })}
          </Button>
        )}
        <Button size="sm" variant="outline" colorScheme="teal" leftIcon={<AddIcon />} onClick={onWriteNew}>
          {t("New requirement")}
        </Button>
      </Flex>
    </Box>
  );
};
